import * as React from "react";
import {
    FilterMetadata,
    FilterOperator,
    FilterOperators,
} from "~/modules/filters/types";
import { getButtonClassName } from "~/components/Button";
import { cn } from "~/lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CSS } from "@dnd-kit/utilities";
import { useDroppable, useDraggable } from "@dnd-kit/core";
import { getDefaultNewFilter } from "./getDefaultNewFilter";
import { getModifiedDragAndDropStyles } from "./getModifiedDragAndDropStyles";
import { isAndOrOperator } from "./functions/isAndOrOperator";
import { EditorFilter } from "./types";
import { FilterForm } from "./FilterForm";
import { generateRandomID } from "~/utils/generateRandomID";
import { FILTER_OPERATOR_TO_LABEL, ROOT_FILTER_ID } from "../consts";
import {
    faChevronDown,
    faLayerGroup,
    faTimes,
} from "@fortawesome/pro-regular-svg-icons";
import { sortEditorFilterArray } from "./functions/sortEditorFilter";
import { buildEditorFilter } from "./functions/buildEditorFilter";
import { FilterMessagesDisplay } from "./FilterMessageDisplay";
import { faFolderPlus, faPlusCircle } from "@fortawesome/pro-solid-svg-icons";
import { FieldSelectPanel } from "./FieldSelectPanel";
import {
    trackFilterListAddCondition,
    trackFilterListAddGroup,
    trackFilterListRemoveGroup,
    trackFilterListToggleAndOr,
} from "../analytics";
import { Modal } from "~/components/Modal";

// // // //

export function AddFilterButtonFilterList(props: {
    currentOperator: FilterOperator;
    filterMetadata: FilterMetadata[];
    compact?: boolean;
    addFilter: (selectedFilter: FilterMetadata) => void;
}) {
    const { filterMetadata, addFilter, compact = false } = props;
    const [open, setOpen] = React.useState(false);

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className={getButtonClassName({
                    size: "sm",
                    className: "gap-3",
                    rounded: true,
                    variant:
                        props.currentOperator === FilterOperators.And
                            ? "and-operator-light"
                            : "or-operator-light",
                })}
            >
                <FontAwesomeIcon icon={faPlusCircle} className="size-4" />
                Add Condition
            </button>
            {/* size="sm" applies no width constraint, so the modal
                shrink-wraps to the panel's own width */}
            <Modal
                show={open}
                size="sm"
                allowScroll={true}
                onHide={() => setOpen(false)}
            >
                {/* The panel's input swallows the first Escape to clear an
                    active search; a bubbled Escape closes the modal */}
                <div
                    onKeyDown={(e) => {
                        if (e.key === "Escape") {
                            setOpen(false);
                        }
                    }}
                >
                    <FieldSelectPanel
                        value={""}
                        compact={compact}
                        filterMetadata={filterMetadata}
                        onChange={(selectedField) => {
                            const selected = filterMetadata.find(
                                (each) => each.field === selectedField,
                            );
                            if (selected) {
                                addFilter(selected);
                                setOpen(false);
                            }
                        }}
                    />
                </div>
            </Modal>
        </>
    );
}

// // // //

export function FilterList(props: {
    filter: EditorFilter;
    onRemove: () => void;
    onChange: (updatedFilter: EditorFilter) => void;
    filterMetadata: FilterMetadata[];
    depth: number;
}) {
    const { filter, filterMetadata } = props;
    const isAndOp = filter.state.operator === FilterOperators.And;
    const isOrOp = filter.state.operator === FilterOperators.Or;

    // // // //

    // Setup draggable list
    const {
        active,
        isDragging,
        activatorEvent,
        // attributes,
        // listeners,
        setNodeRef,
        transform,
    } = useDraggable({
        id: filter.id,
        data: {
            ...filter.state,
        },
    });

    // Setup droppable zone
    const {
        isOver,
        over,
        setNodeRef: setDroppableNodeRef,
    } = useDroppable({
        id: filter.id,
        data: {
            ...filter.state,
        },
    });

    // Pull parentID from the ID of the item currently being dragged over
    const parentID = over?.data?.current?.parentID || "";

    // // // //

    const style: React.CSSProperties = {
        opacity: isDragging ? 0.8 : undefined,
        transform: CSS.Transform.toString(transform),
        // transition,
    };

    if (isOver) {
        // console.log("IS OVER");
        // console.log(over);
        // console.log(over?.id === filter.id);
        // console.log(active?.id === filter.id);
        if (active?.id !== filter.id) {
            if (
                active?.data?.current?.operator &&
                isAndOrOperator(active?.data?.current?.operator)
            ) {
                // if (transform?.x)
                // console.log(index);
                // console.log(parent.nested.length);
                // console.log(active?.data.current.index);

                // Check if in the same parent
                // console.log(transform?.y);
                // @ts-ignore
                // console.log(activatorEvent?.offsetY || "");

                // @ts-ignore
                const offsetY = activatorEvent?.offsetY || 0;

                // Shift the hovered group by the dragged item's actual height so
                // the gap that opens up lines up with the item being dropped in.
                const dragHeight = active?.rect.current.initial?.height ?? 50;

                if (offsetY < 20) {
                    style.transform = `translateY(-${dragHeight}px)`;
                } else {
                    style.transform = `translateY(${dragHeight}px)`;
                }

                style.transition = CSS.Transition.toString({
                    duration: 100,
                    easing: "ease",
                    property: "transform",
                });
            }
        }
    }

    return (
        <div
            key={filter.id}
            ref={setNodeRef}
            style={getModifiedDragAndDropStyles(style, { isDragging })}
            className={cn(
                "flex flex-col gap-1 w-full py-2 px-2 border rounded-2xl",
                {
                    "shadow-lg": active?.id,
                    "bg-card": !isOver && filter.id !== parentID,
                    "bg-blue-100":
                        (isOver || filter.id === parentID) && isAndOp,
                    "bg-violet-100":
                        (isOver || filter.id === parentID) && isOrOp,
                    "border-blue-400": isAndOp,
                    "border-violet-400": isOrOp,
                    "shadow-sm transition-colors": true,
                    "border-l-blue-400 border-l-8": isAndOp,
                    "border-l-violet-400 border-l-8": isOrOp,
                },
            )}
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        className={cn(
                            "min-w-16 text-white rounded-full transition-colors select-none flex justify-between items-center",
                            {
                                "bg-blue-400 hover:bg-primary":
                                    filter.state.operator ===
                                    FilterOperators.And,
                                "bg-violet-400 hover:bg-violet-500":
                                    filter.state.operator ===
                                    FilterOperators.Or,
                            },
                        )}
                        onClick={() => {
                            const updatedOperator =
                                filter.state.operator === FilterOperators.And
                                    ? FilterOperators.Or
                                    : FilterOperators.And;

                            trackFilterListToggleAndOr({
                                prevOperator: filter.state.operator,
                                nextOperator: updatedOperator,
                            });

                            props.onChange({
                                ...filter,
                                state: {
                                    ...filter.state,
                                    operator: updatedOperator,
                                },
                            });
                        }}
                    >
                        <span className="pl-2">
                            {FILTER_OPERATOR_TO_LABEL[filter.state.operator]}
                        </span>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className="h-3 mr-1"
                        />
                    </button>
                    <p
                        className={cn(
                            "whitespace-nowrap select-none text-sm -ml-2",
                            {
                                "text-blue-400":
                                    filter.state.operator ===
                                    FilterOperators.And,
                                "text-violet-400":
                                    filter.state.operator ===
                                    FilterOperators.Or,
                            },
                        )}
                    >
                        {filter.state.operator === FilterOperators.Or &&
                            "Match Any"}
                        {filter.state.operator === FilterOperators.And &&
                            "Match All"}
                    </p>

                    <FilterMessagesDisplay
                        filter={filter}
                        onRemove={() => props.onRemove()}
                        onChange={(updatedEditorFilter: EditorFilter) => {
                            props.onChange(updatedEditorFilter);
                        }}
                    />
                </div>

                <div className="flex items-center gap-1">
                    <button
                        className={cn(
                            "px-1 py-0 text-base group font-light flex items-center gap-1.5 transition-colors",
                            {
                                "text-blue-400": isAndOp,
                                "text-violet-400": isOrOp,
                            },
                        )}
                        onClick={() => {
                            trackFilterListRemoveGroup();
                            props.onRemove();
                        }}
                    >
                        <span className="transition-opacity opacity-0 group-hover:opacity-100">
                            {filter.id === ROOT_FILTER_ID
                                ? "Reset Filters"
                                : "Remove"}
                        </span>
                        <FontAwesomeIcon
                            icon={faTimes}
                            className="h-5 inline"
                        />
                    </button>
                </div>
            </div>

            <div className="flex flex-col gap-1" ref={setDroppableNodeRef}>
                {/* TODO - simplify this logic, or pull into a separate component */}
                {/* TODO - simplify this logic, or pull into a separate component */}
                {/* TODO - simplify this logic, or pull into a separate component */}
                {(filter.nested.length === 0 ||
                    filter.nested.every((e) =>
                        isAndOrOperator(e.state.operator),
                    )) && (
                    <div className="select-none p-3 w-full flex justify-center items-center border-dashed border-2 border-slate-300 mt-1 text-slate-300 font-semibold rounded-lg">
                        <FontAwesomeIcon
                            icon={faLayerGroup}
                            className="h-6 w-6 mr-2"
                        />
                        {<>Drop Filters Here</>}
                    </div>
                )}

                {filter.nested.map((f, filterIndex) => {
                    if (isAndOrOperator(f.state.operator)) {
                        return (
                            <FilterList
                                key={f.id}
                                filter={f}
                                depth={props.depth + 1}
                                filterMetadata={filterMetadata}
                                onRemove={() => {
                                    props.onChange({
                                        ...filter,
                                        nested: sortEditorFilterArray([
                                            ...filter.nested.filter(
                                                (_prevFilter, fIndex) => {
                                                    return (
                                                        fIndex !== filterIndex
                                                    );
                                                },
                                            ),
                                        ]),
                                    });
                                }}
                                onChange={(updatedFilter) => {
                                    // Remove parent filter if it has no filters left - keep this?
                                    // if (
                                    //     updatedFilter.nested &&
                                    //     updatedFilter.nested.length === 0
                                    // ) {
                                    //     props.onChange({
                                    //         ...filter,
                                    //         nested: sortEditorFilterArray([
                                    //             ...filter.nested.filter(
                                    //                 (_prevFilter, fIndex) => {
                                    //                     return (
                                    //                         fIndex !==
                                    //                         filterIndex
                                    //                     );
                                    //                 },
                                    //             ),
                                    //         ]),
                                    //     });
                                    //     return;
                                    // }

                                    props.onChange({
                                        ...filter,
                                        nested: sortEditorFilterArray([
                                            ...filter.nested.map(
                                                (prevFilter, fIndex) => {
                                                    if (
                                                        fIndex === filterIndex
                                                    ) {
                                                        return updatedFilter;
                                                    }
                                                    return prevFilter;
                                                },
                                            ),
                                        ]),
                                    });
                                }}
                            />
                        );
                    }

                    return (
                        <FilterForm
                            key={f.id}
                            filter={f}
                            index={filterIndex}
                            parent={filter}
                            filterMetadata={filterMetadata}
                            onClone={() => {
                                props.onChange({
                                    ...filter,
                                    nested: sortEditorFilterArray([
                                        ...filter.nested,
                                        {
                                            ...f,
                                            id: generateRandomID(),
                                        },
                                    ]),
                                });
                            }}
                            onChange={(updatedFilter: EditorFilter) => {
                                props.onChange({
                                    ...filter,
                                    nested: sortEditorFilterArray([
                                        ...filter.nested.map(
                                            (prevFilter, fIndex) => {
                                                if (fIndex === filterIndex) {
                                                    return updatedFilter;
                                                }
                                                return prevFilter;
                                            },
                                        ),
                                    ]),
                                });
                            }}
                            onRemove={() => {
                                props.onChange({
                                    ...filter,
                                    nested: sortEditorFilterArray([
                                        ...filter.nested.filter(
                                            (_f, fIndex) => {
                                                return fIndex !== filterIndex;
                                            },
                                        ),
                                    ]),
                                });
                            }}
                        />
                    );
                })}

                <div className="flex items-center gap-1.5 justify-end mt-1">
                    {props.depth < 2 && (
                        <button
                            disabled={props.depth >= 2}
                            className={getButtonClassName({
                                size: "sm",
                                className: "gap-3",
                                rounded: true,
                                variant: isAndOp
                                    ? "and-operator-light"
                                    : "or-operator-light",
                            })}
                            onClick={() => {
                                trackFilterListAddGroup();

                                props.onChange({
                                    ...filter,
                                    nested: sortEditorFilterArray([
                                        ...filter.nested,
                                        buildEditorFilter({
                                            filter: {
                                                operator:
                                                    filter.state.operator ===
                                                    FilterOperators.Or
                                                        ? FilterOperators.And
                                                        : FilterOperators.Or,
                                                filters: [],
                                            },
                                            isRoot: false,
                                        }),
                                    ]),
                                });
                            }}
                        >
                            <FontAwesomeIcon
                                icon={faFolderPlus}
                                className="size-4"
                            />
                            Add Group
                        </button>
                    )}

                    <AddFilterButtonFilterList
                        currentOperator={filter.state.operator}
                        filterMetadata={filterMetadata}
                        addFilter={(selectedFilter: FilterMetadata) => {
                            trackFilterListAddCondition();

                            props.onChange({
                                ...filter,
                                nested: sortEditorFilterArray([
                                    ...filter.nested,
                                    buildEditorFilter({
                                        filter: getDefaultNewFilter([
                                            selectedFilter,
                                        ]),
                                        isRoot: false,
                                    }),
                                ]),
                            });
                        }}
                    />
                </div>
            </div>
        </div>
    );
}
